/**
 * Workbench API service (Plan 152).
 *
 * Per-user precedent document library — sibling of playbookApi.
 */

import { apiClient } from './client';
import type {
  UpdateWorkbenchClauseNotesRequest,
  UpdateWorkbenchDocumentRequest,
  WorkbenchAttachmentRow,
  WorkbenchClause,
  WorkbenchDocument,
} from '../../types/workbench';

const BASE = '/api/v1';

class WorkbenchAPI {
  // ── Documents ──────────────────────────────────────────────────

  async listDocuments(): Promise<WorkbenchDocument[]> {
    const { data } = await apiClient.get<WorkbenchDocument[]>(
      `${BASE}/workbench/documents`,
    );
    return data;
  }

  async getDocument(id: string): Promise<WorkbenchDocument> {
    const { data } = await apiClient.get<WorkbenchDocument>(
      `${BASE}/workbench/documents/${id}`,
    );
    return data;
  }

  async uploadDocument(params: {
    file: File;
    title: string;
    tags?: string[];
  }): Promise<WorkbenchDocument> {
    const form = new FormData();
    form.append('file', params.file);
    form.append('title', params.title);
    form.append('tags', JSON.stringify(params.tags ?? []));
    const { data } = await apiClient.post<WorkbenchDocument>(
      `${BASE}/workbench/documents`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  }

  async updateDocument(
    id: string,
    payload: UpdateWorkbenchDocumentRequest,
  ): Promise<WorkbenchDocument> {
    const { data } = await apiClient.patch<WorkbenchDocument>(
      `${BASE}/workbench/documents/${id}`,
      payload,
    );
    return data;
  }

  async deleteDocument(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/workbench/documents/${id}`);
  }

  async retriggerExtraction(id: string): Promise<{ status: string; document_id: string }> {
    const { data } = await apiClient.post<{ status: string; document_id: string }>(
      `${BASE}/workbench/documents/${id}/extract`,
    );
    return data;
  }

  // ── Clauses ────────────────────────────────────────────────────

  async listClauses(documentId: string): Promise<WorkbenchClause[]> {
    const { data } = await apiClient.get<WorkbenchClause[]>(
      `${BASE}/workbench/documents/${documentId}/clauses`,
    );
    return data;
  }

  async updateClauseNotes(
    clausePk: string,
    payload: UpdateWorkbenchClauseNotesRequest,
  ): Promise<WorkbenchClause> {
    const { data } = await apiClient.patch<WorkbenchClause>(
      `${BASE}/workbench/clauses/${clausePk}/notes`,
      payload,
    );
    return data;
  }

  // ── Attachments ────────────────────────────────────────────────

  async listMatterAttachments(matterId: string): Promise<WorkbenchAttachmentRow[]> {
    const { data } = await apiClient.get<WorkbenchAttachmentRow[]>(
      `${BASE}/matters/${matterId}/workbench-attachments`,
    );
    return data;
  }

  async attach(matterId: string, workbenchDocumentId: string): Promise<WorkbenchAttachmentRow> {
    const { data } = await apiClient.post<WorkbenchAttachmentRow>(
      `${BASE}/matters/${matterId}/workbench-attachments`,
      { workbench_document_id: workbenchDocumentId },
    );
    return data;
  }

  async detach(matterId: string, workbenchDocumentId: string): Promise<void> {
    await apiClient.delete(
      `${BASE}/matters/${matterId}/workbench-attachments/${workbenchDocumentId}`,
    );
  }
}

export const workbenchApi = new WorkbenchAPI();
